import { isImage } from "@common/lib/image";
import { Editor, Node, Transforms } from "slate";
import { insertOrReplaceNode, isHeadingNode } from "./lib";
import { type SlateEditor } from "./types";

export const withInlines = (editor: SlateEditor) => {
  const { isInline } = editor;

  editor.isInline = (element) => ["a"].includes(element.type) || isInline(element);

  return editor;
};

export const withHelpers = (editor: SlateEditor) => {
  editor.deleteAll = () => {
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, [])
      }
    });
  };

  editor.insertNodeAt = (node: Node, index: number, select?: boolean) => {
    Transforms.insertNodes(editor, node, {
      at: [index],
      select
    });
  };

  return editor;
};

/** Make the elements of the passed types available for selection */
export const withSelectableTypes = (editor: SlateEditor, types: string[]) => {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    return types.includes(element.type) ? true : isVoid(element);
  };

  return editor;
};

/** Redefining the logic of creating a new line under the header. The new line will be a regular paragraph, not a title. */
export const withBreakTitle = (editor: SlateEditor) => {
  const { insertBreak } = editor;
  editor.insertBreak = () => {
    const { selection } = editor;
    if (selection) {
      const [title] = Editor.nodes(editor, {
        match: isHeadingNode
      });

      if (title) {
        Transforms.insertNodes(editor, {
          children: [{ text: "" }],
          type: "p"
        });
        return;
      }
    }
    insertBreak();
  };

  return editor;
};

export const withImages = (editor: SlateEditor) => {
  const { insertData, isVoid } = editor;

  editor.isVoid = (element) => {
    return element.type === "image" ? true : isVoid(element);
  };

  editor.insertData = (data) => {
    const text = data.getData("text/plain");
    const { files } = data;

    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        if (file.size > 100000) {
          alert("Maximum inline image size is 100Kb");
          return;
        }

        const reader = new FileReader();
        const [mime] = file.type.split("/");

        if (mime === "image") {
          reader.addEventListener("load", () => {
            const url = reader.result;
            insertOrReplaceNode(editor, null, { type: "image", url: url as string, children: [{ text: "" }] });
          });

          reader.readAsDataURL(file);
        }
      });
    } else if (text && isImage(text)) {
      insertOrReplaceNode(editor, null, { type: "image", url: text, children: [{ text: "" }] });
    } else {
      insertData(data);
    }
  };

  return editor;
};
